import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { ApiResponse } from "models";

// ================== Group State ==================
export interface GroupState {
  // Group-related
  loading: boolean;
  success: boolean;
  message: string;
  data: any;
  groupByIdLoading: boolean;
  groupByIdSuccess: boolean;
  groupByIdData: any;

  // Device-related
  deviceLoading: boolean;
  deviceSuccess: boolean;
  deviceMessage: string;
  deviceData: any;

  // Device by ID
  deviceByIdLoading: boolean;
  deviceByIdSuccess: boolean;
  deviceByIdMessage: string;
  deviceByIdData: any;

  // Add Device
  addDeviceLoading: boolean;
  addDeviceSuccess: boolean;
  addDeviceMessage: string;

  // Update Device
  updateDeviceLoading: boolean;
  updateDeviceSuccess: boolean;
  updateDeviceMessage: string;

  // Delete Device-related
  deleteDeviceLoading: boolean;
  deleteDeviceSuccess: boolean;
  deleteDeviceMessage: string;
}

const initialState: GroupState = {
  // Group defaults
  loading: false,
  success: false,
  message: "",
  data: {},
  groupByIdLoading: false,
  groupByIdSuccess: false,
  groupByIdData: {},

  // Device defaults
  deviceLoading: false,
  deviceSuccess: false,
  deviceMessage: "",
  deviceData: {},

  // Device by ID defaults
  deviceByIdLoading: false,
  deviceByIdSuccess: false,
  deviceByIdMessage: "",
  deviceByIdData: {},

  // Add Device defaults
  addDeviceLoading: false,
  addDeviceSuccess: false,
  addDeviceMessage: "",

  // Update Device defaults
  updateDeviceLoading: false,
  updateDeviceSuccess: false,
  updateDeviceMessage: "",

  // Delete Device defaults
  deleteDeviceLoading: false,
  deleteDeviceSuccess: false,
  deleteDeviceMessage: "",
};

const groupSlice = createSlice({
  name: "group",
  initialState,
  reducers: {
    // ================== Group ==================
    getGroupList(state, _action: PayloadAction<{ search?: string }>) {
      state.loading = true;
      state.message = "";
    },
    getGroupListSuccess(state, action: PayloadAction<ApiResponse>) {
      state.loading = false;
      state.data = action.payload.data;
      state.success = action.payload.success;
    },
    getGroupListFailed(state, action: PayloadAction<ApiResponse>) {
      state.loading = false;
      state.data = {};
      state.message = action.payload.message;
      state.success = action.payload.success;
    },

    // ================== Devices ==================
    getDeviceList(state, _action: PayloadAction<any>) {
      state.deviceLoading = true;
      state.deviceMessage = "";
    },
    getDeviceListSuccess(state, action: PayloadAction<ApiResponse>) {
      state.deviceLoading = false;
      state.deviceData = action.payload;
      state.deviceSuccess = action.payload.success;
    },
    getDeviceListFailed(state, action: PayloadAction<ApiResponse>) {
      state.deviceLoading = false;
      state.deviceData = {};
      state.deviceMessage = action.payload.message;
      state.deviceSuccess = action.payload.success;
    },

    // ================== Device By ID ==================
    getDeviceById(state, _action: PayloadAction<string>) {
      state.deviceByIdLoading = true;
      state.deviceByIdMessage = "";
    },
    getDeviceByIdSuccess(state, action: PayloadAction<ApiResponse>) {
      state.deviceByIdLoading = false;
      state.deviceByIdData = action.payload.data;
      state.deviceByIdSuccess = action.payload.success;
    },
    getDeviceByIdFailed(state, action: PayloadAction<ApiResponse>) {
      state.deviceByIdLoading = false;
      state.deviceByIdData = {};
      state.deviceByIdMessage = action.payload.message;
      state.deviceByIdSuccess = false;
    },

    // ================== Add Device ==================
    addDevice(state, _action: PayloadAction<any>) {
      state.addDeviceLoading = true;
      state.addDeviceMessage = "";
    },
    addDeviceSuccess(state, action: PayloadAction<ApiResponse>) {
      state.addDeviceLoading = false;
      state.addDeviceSuccess = action.payload.success;
      state.addDeviceMessage = action.payload.message;
    },
    addDeviceFailed(state, action: PayloadAction<ApiResponse>) {
      state.addDeviceLoading = false;
      state.addDeviceSuccess = false;
      state.addDeviceMessage = action.payload.message;
    },

    // ================== Update Device ==================
    updateDevice(state, _action: PayloadAction<any>) {
      state.updateDeviceLoading = true;
      state.updateDeviceMessage = "";
    },
    updateDeviceSuccess(state, action: PayloadAction<ApiResponse>) {
      state.updateDeviceLoading = false;
      state.updateDeviceSuccess = action.payload.success;
      state.updateDeviceMessage = action.payload.message;
    },
    updateDeviceFailed(state, action: PayloadAction<ApiResponse>) {
      state.updateDeviceLoading = false;
      state.updateDeviceSuccess = false;
      state.updateDeviceMessage = action.payload.message;
    },

    // ================== Delete Device ==================
    deleteDevice(state, _action: PayloadAction<{ id: string }>) {
      state.deleteDeviceLoading = true;
      state.deleteDeviceMessage = "";
    },
    deleteDeviceSuccess(state, action: PayloadAction<ApiResponse>) {
      state.deleteDeviceLoading = false;
      state.deleteDeviceSuccess = action.payload.success;
      state.deleteDeviceMessage = action.payload.message;
    },
    deleteDeviceFailed(state, action: PayloadAction<ApiResponse>) {
      state.deleteDeviceLoading = false;
      state.deleteDeviceSuccess = false;
      state.deleteDeviceMessage = action.payload.message;
    },

    // ================== Clear All ==================
    clearGroupData(state) {
      // Group reset
      state.loading = false;
      state.success = false;
      state.message = "";
      state.data = {};

      // Device list reset
      state.deviceLoading = false;
      state.deviceSuccess = false;
      state.deviceMessage = "";
      state.deviceData = {};

      // Device by ID reset
      state.deviceByIdLoading = false;
      state.deviceByIdSuccess = false;
      state.deviceByIdMessage = "";
      state.deviceByIdData = {};

      // Add Device reset
      state.addDeviceLoading = false;
      state.addDeviceSuccess = false;
      state.addDeviceMessage = "";

      // Update Device reset
      state.updateDeviceLoading = false;
      state.updateDeviceSuccess = false;
      state.updateDeviceMessage = "";

      // Delete Device reset
      state.deleteDeviceLoading = false;
      state.deleteDeviceSuccess = false;
      state.deleteDeviceMessage = "";
    },
  },
});

export const groupAction = groupSlice.actions;

// ================== Selectors ==================
export const getGroupData = (state: RootState) => state.groupModule.data;
export const getGroupLoading = (state: RootState) => state.groupModule.loading;
export const getGroupSuccess = (state: RootState) => state.groupModule.success;
export const getGroupMessage = (state: RootState) => state.groupModule.message;

export const getDeviceData = (state: RootState) => state.groupModule.deviceData;
export const getDeviceLoading = (state: RootState) => state.groupModule.deviceLoading;
export const getDeviceSuccess = (state: RootState) => state.groupModule.deviceSuccess;
export const getDeviceMessage = (state: RootState) => state.groupModule.deviceMessage;

export const getDeviceByIdData = (state: RootState) => state.groupModule.deviceByIdData;
export const getDeviceByIdLoading = (state: RootState) => state.groupModule.deviceByIdLoading;
export const getDeviceByIdSuccess = (state: RootState) => state.groupModule.deviceByIdSuccess;
export const getDeviceByIdMessage = (state: RootState) => state.groupModule.deviceByIdMessage;

export const getAddDeviceLoading = (state: RootState) => state.groupModule.addDeviceLoading;
export const getAddDeviceSuccess = (state: RootState) => state.groupModule.addDeviceSuccess;
export const getAddDeviceMessage = (state: RootState) => state.groupModule.addDeviceMessage;

export const getUpdateDeviceLoading = (state: RootState) => state.groupModule.updateDeviceLoading;
export const getUpdateDeviceSuccess = (state: RootState) => state.groupModule.updateDeviceSuccess;
export const getUpdateDeviceMessage = (state: RootState) => state.groupModule.updateDeviceMessage;

export const getDeleteDeviceLoading = (state: RootState) => state.groupModule.deleteDeviceLoading;
export const getDeleteDeviceSuccess = (state: RootState) => state.groupModule.deleteDeviceSuccess;
export const getDeleteDeviceMessage = (state: RootState) => state.groupModule.deleteDeviceMessage;

const groupReducer = groupSlice.reducer;
export default groupReducer;
